import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { UnifiedModal } from '@/components/common/UnifiedModal/UnifiedModal';
import { RadioButton } from '@/components/residents/marketplace/shared';
import { CORK_BOARD_REPORT_REASONS, CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import type { ReportReason } from '@/types/residents/corkBoard';

interface CorkBoardReportModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (reason: ReportReason, details?: string) => void;
  isSubmitting: boolean;
  hasAlreadyReported: boolean;
  contentType: 'thread' | 'reply';
}

export const CorkBoardReportModal: React.FC<CorkBoardReportModalProps> = ({
  visible,
  onClose,
  onSubmit,
  isSubmitting,
  hasAlreadyReported,
  contentType,
}) => {
  const { semantic, components, colors } = useThemeColors();
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');

  const handleSubmit = useCallback(() => {
    if (selectedReason && !isSubmitting) {
      onSubmit(selectedReason, details.trim() || undefined);
      setSelectedReason(null);
      setDetails('');
    }
  }, [selectedReason, details, isSubmitting, onSubmit]);

  const handleClose = useCallback(() => {
    setSelectedReason(null);
    setDetails('');
    onClose();
  }, [onClose]);

  if (hasAlreadyReported) {
    const footer = (
      <TouchableOpacity style={[styles.submitButton, { backgroundColor: components.button.primaryBg }]} onPress={handleClose} activeOpacity={0.8}>
        <Text style={[styles.submitButtonText, { color: components.button.primaryText }]}>Close</Text>
      </TouchableOpacity>
    );

    return (
      <UnifiedModal
        visible={visible}
        onClose={handleClose}
        variant="dialog"
        showHeader={true}
        headerStyle={{ borderBottomWidth: 0 }}
        footer={footer}
      >
        <View style={styles.alreadyReportedContent}>
          <Text style={[styles.alreadyReportedTitle, { color: semantic.text.primary }]}>Report submitted</Text>
          <Text style={[styles.alreadyReportedDescription, { color: semantic.text.secondary }]}>
            You have already reported this {contentType}. Our moderation team will review it.
          </Text>
        </View>
      </UnifiedModal>
    );
  }

  const isDisabled = !selectedReason || isSubmitting;
  const footer = (
    <TouchableOpacity
      style={[styles.submitButton, { backgroundColor: components.button.primaryBg }, isDisabled && { backgroundColor: components.button.disabledBg }]}
      onPress={handleSubmit}
      activeOpacity={0.8}
      disabled={isDisabled}
    >
      {isSubmitting ? (
        <ActivityIndicator size="small" color={semantic.text.primary} />
      ) : (
        <Text style={[styles.submitButtonText, { color: components.button.primaryText }, isDisabled && { color: components.button.disabledText }]}>
          Submit report
        </Text>
      )}
    </TouchableOpacity>
  );

  return (
    <UnifiedModal
      visible={visible}
      onClose={handleClose}
      variant="dialog"
      showHeader={true}
      headerStyle={{ borderBottomWidth: 0 }}
      footer={footer}
      scrollable={true}
      avoidKeyboard={true}
    >
      <View style={styles.titleSection}>
        <Text style={[styles.title, { color: semantic.text.primary }]}>Report this {contentType}</Text>
        <Text style={[styles.subtitle, { color: semantic.text.secondary }]}>
          Why are you reporting this {contentType}? Select the reason that best applies.
        </Text>
      </View>

      <View style={styles.optionsList}>
        {CORK_BOARD_REPORT_REASONS.map((reason) => (
          <TouchableOpacity
            key={reason.value}
            style={[styles.optionItem, { backgroundColor: colors.glass.buttonBg }]}
            onPress={() => setSelectedReason(reason.value)}
            activeOpacity={0.8}
          >
            <Text style={[
              styles.optionLabel,
              { color: semantic.text.primary },
              selectedReason === reason.value && styles.optionLabelSelected,
            ]}>
              {reason.label}
            </Text>
            <RadioButton isSelected={selectedReason === reason.value} />
          </TouchableOpacity>
        ))}

        <View style={[styles.detailsContainer, { backgroundColor: semantic.background.input, borderColor: semantic.border.secondary }]}>
          <TextInput
            style={[styles.detailsInput, { color: semantic.text.primary }]}
            placeholder="Additional details (optional)"
            placeholderTextColor={semantic.text.muted}
            value={details}
            onChangeText={setDetails}
            multiline
            textAlignVertical="top"
            maxLength={CORK_BOARD_INPUT_LIMITS.reportDetailsMax}
          />
        </View>
      </View>
    </UnifiedModal>
  );
};

const styles = StyleSheet.create({
  titleSection: {
    alignItems: 'center',
    gap: 6,
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    lineHeight: 36,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    textAlign: 'center',
  },
  optionsList: {
    gap: 16,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  optionLabel: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
    flex: 1,
  },
  optionLabelSelected: {
    fontWeight: '500',
  },
  detailsContainer: {
    borderRadius: 8,
    borderWidth: 1,
    height: 80,
  },
  detailsInput: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 11,
    paddingBottom: 11,
    fontSize: 14,
    lineHeight: 17.5,
  },
  submitButton: {
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  alreadyReportedContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  alreadyReportedTitle: {
    fontSize: 24,
    fontWeight: '600',
    lineHeight: 36,
    textAlign: 'center',
  },
  alreadyReportedDescription: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    textAlign: 'center',
  },
});
